<template>
  <b-modal
    id="add-or-update"
    :visible="isAddOrUpdateActive"
    centered
    hide-footer
    hide-header
    no-close-on-esc
    no-close-on-backdrop
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-or-update-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header py-1"
          style="border-bottom: 1px solid #000000"
        >
          <h5
            class="mb-0"
            style="font-weight: 400; font-size: 14px; line-height: 160%"
          >
            {{ isUpdate ? "Update Application" : "Add New Application" }}
          </h5>

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form
            class="my-1"
            @submit.prevent="handleSubmit(onCreateOrUpdate)"
            @reset.prevent="resetForm"
          >
            <b-alert v-if="errorMessage" variant="danger" :show="true">
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>

            <div class="mb-2">
              <b-media class="text-center">
                <b-avatar
                  ref="previewEl"
                  badge-variant="transparent"
                  :text="sentenceCase(avatarText(`${new_data ? new_data.title : ''}`))"
                  :src="logoPath"
                  :variant="logoPath ? '' : 'primary'"
                  size="10rem" 
                  square
                >
                  <template #badge>
                    <b-button variant="primary" class="btn-icon rounded-circle" @click="$refs.avatar.$el.childNodes[0].click()">
                      <feather-icon icon="CameraIcon" />
                    </b-button>
                  </template>
                </b-avatar>
              </b-media>

              <div>
                <b-form-file v-show="false" ref="avatar" accept="image/*" placeholder="" no-drop @input="onAvatarRenderer" />
              </div>
            </div>

            <validation-provider
              #default="validationContext"
              name="Title"
              rules="required"
            >
              <b-form-group label="Title" label-for="title">
                <template #label>
                  <span>Title<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-input
                  id="title"
                  v-model="new_data.title"
                  autofocus
                  placeholder=""
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              name="URL"
              rules="required"
            >
              <b-form-group label="URL" label-for="url">
                <template #label>
                  <span>Application URL<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-input
                  id="full_url"
                  v-model="new_data.full_url"
                  autofocus
                  placeholder=""
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group label="Access Type" label-for="Access Type">
              <template #label>
                <span>Access Type<span style="color: tomato">*</span></span>
              </template>

              <validation-provider
                #default="{ errors }"
                name="Access Type"
                rules="required"
              >
                <v-select
                  id="Access Type"
                  v-model="new_data.access_type"
                  :loading="loading"
                  :clearable="false"
                  :options="accessTypeOptions"
                  :reduce="(val) => val.value"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group label="Status" label-for="Status">
              <template #label>
                <span>Status<span style="color: tomato">*</span></span>
              </template>

              <validation-provider
                #default="{ errors }"
                name="Status"
                rules="required"
              >
                <v-select
                  id="Status"
                  v-model="new_data.status"
                  :loading="loading"
                  :clearable="false"
                  :options="statusOptions"
                  :reduce="(val) => val.value"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Description -->
            <validation-provider
              #default="validationContext"
              name="Description"
              rules=""
            >
              <b-form-group label="Description" label-for="description">
                <b-form-textarea
                  id="description"
                  v-model="new_data.description"
                  :state="getValidationState(validationContext)"
                  :disabled="disableForm"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group label="" label-for="Access Type">
              <validation-provider
                #default="{ errors }"
              >
              <b-form-checkbox v-model="new_data.require_otp">
                Require OTP
              </b-form-checkbox>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                {{ isUpdate ? "Update" : "Add" }}
              </b-button>

              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay,
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BButton,
  BFormFile,
  BFormCheckbox,
  BAlert,
  BCardText,
  BModal,
  BMedia,
  BMediaBody,
  BMediaAside,
  BImg,
  BAvatar
} from "bootstrap-vue";

import { required } from "@validations";
import { get, cloneDeep, omit } from "lodash";
import { ValidationProvider, ValidationObserver } from "vee-validate";

import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

const rawApplication = {
  title: "",
  description: "",
  full_url: "",
  access_type: "",
  require_otp: true
};

export default {
  components: {
    BImg,
    BForm,
    BAlert,
    BModal,
    BMedia,
    BButton,
    vSelect,
    BAvatar,
    BOverlay,
    BSidebar,
    BCardText,
    BFormFile,
    BFormGroup,
    BMediaBody,
    BFormInput,
    BMediaAside,
    BFormCheckbox,
    BFormTextarea,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isAddOrUpdateActive",
    event: "update:is-add-or-update-active",
  },
  props: {
    isAddOrUpdateActive: {
      type: Boolean,
      required: true,
    },
    updateData: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      new_data: cloneDeep(rawApplication),
      loading: false,
      errorMessage: "",
      required,
      accessTypeOptions: [
        { label: "Public", value: "public" },
        { label: "Private", value: "private" },
        { label: "External", value: "external" },
      ],
      statusOptions: [
        { label: "Active", value: "active" },
        { label: "Draft", value: "draft" },
        { label: "Inactive", value: "inactive" },
      ],
      logo: null,
      logoPath: "",
    };
  },
  computed: {
    isUpdate() {
      return !!this.updateData;
    },
    disableForm() {
      if (this.isUpdate) {
        return !this.can("applications:CanUpdate");
      }

      return !this.can("applications:CanAddNew");
    },
  },
  watch: {
    updateData: {
      handler(updateData) {
        if (updateData) {
          this.new_data = {
            _id: updateData._id,
            title: updateData.title,
            description: updateData.description,
            full_url: updateData.full_url,
            access_type: updateData.access_type,
            status: updateData.status,
            require_otp: updateData.require_otp,
          };
          this.logoPath = updateData.logo
        } else {
          this.logoPath = ""
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async onCreateOrUpdate() {
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success) {
          return;
        }

        const messages = {
          update: {
            title: "Confirm Application Update",
            text: "You are about to update details of an Application.",
            icon: "warning",
          },
          new: {
            title: "Create Application",
            text: "You are about to create an Application",
            icon: "info",
          },
        };

        /* eslint-disable no-nested-ternary */
        const message = this.isUpdate ? messages.update : messages.new;
        const { title, text, icon } = message;

        const result = await this.$swal({
          title,
          text,
          icon,
          showCancelButton: true,
          confirmButtonText: "Yes, proceed.",
          customClass: {
            confirmButton: "btn btn-primary",
            cancelButton: "btn btn-outline-danger ml-1",
          },
          buttonsStyling: false,
        });
        if (!result.isConfirmed) {
          return;
        }

        const makeFormData = (payload) => {
          const form = new FormData()
          Object.entries(payload).forEach(([key, value]) => {
            if (key !== 'logo') {
              form.append(key, value)
            }
          })
          if (this.logo) {
            form.append("logo", this.logo)
          }
          return form
        }

        if (this.isUpdate) {
          const id = this.new_data._id;
          const update_data = omit(this.new_data, ["_id", "logo"]);

          if (!this.can("applications:CanUpdate")) {
            return;
          }

          await this.useJwt().mainService.applications.update(id, makeFormData(update_data));
        } else {
          if (!this.can("applications:CanAddNew")) {
            return;
          }

          await this.useJwt().mainService.applications.create(makeFormData(this.new_data));
        }
        this.$emit("refetchData");
        this.$emit("update:is-add-or-update-active", false);

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success!",
            icon: "AlertQuestionIcon",
            variant: "success",
            text: `Member ${this.isUpdate ? "updated" : "added"} successfully`,
          },
        });

        this.resetForm();
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onAvatarRenderer(file) {
     const reader = new FileReader()

      reader.addEventListener(
        'load',
        () => {
          this.logoPath = reader.result
          this.logo = file;
        },
        false,
      )

      if (file) {
        reader.readAsDataURL(file)
      }
    },
    resetForm() {
      this.$emit("resetUpdateData");
      this.logoPath = "";
      this.logo = null;
      this.new_data = {};
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

#add-or-update {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
